import { create } from 'zustand';
import { TOKEN_KEY, REFRESH_TOKEN_KEY } from '../../../shared/constants/config';
import { useSidebarVisibilityStore } from '../../../shared/store/sidebarVisibilityStore';
import { useBulkImportJobStore } from '../../../shared/store/bulkImportJobStore';
import { clearReviewOrderCopy } from '../../../shared/utils/reviewOrderStorage';

const USER_KEY = 'auth_user';
const SELECTED_COMPANY_KEY = 'selected_company';
const SELECTED_SITE_KEY = 'selected_site';

/**
 * Clears the auth-related localStorage entries removed on logout.
 */
function clearAuthStorage() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(SELECTED_COMPANY_KEY);
  localStorage.removeItem(SELECTED_SITE_KEY);
  // Onboarding bootstrap triplet — written by UserOnboardPage so the next KeycloakProvider
  // bootstrap seeds keycloak-js with these tokens. Consumption is sub-second (the full-page
  // navigate to /dashboard triggers the bootstrap which immediately deletes them), so in practice
  // a logout rarely finds them in place; cleared here anyway as defence-in-depth so an abandoned
  // onboarding link can't leave an id/refresh token sitting in storage after a sign-out.
  localStorage.removeItem('kc_boot_token');
  localStorage.removeItem('kc_boot_refresh_token');
  localStorage.removeItem('kc_boot_id_token');
  // The checkout review page's refresh copy of an in-progress order — must not survive into the
  // next login in this tab.
  clearReviewOrderCopy();
}

/**
 * @typedef {Object} User
 * @property {string} id
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} email
 * @property {string} companyName
 * @property {string} role
 */

/**
 * @typedef {Object} Company
 * @property {string|number} id
 * @property {string} name
 */

/**
 * @typedef {Object} Site
 * @property {number} haloPsaSiteId
 * @property {string} siteName
 */

/**
 * @typedef {Object} AuthStore
 * @property {User|null} user
 * @property {string|null} token
 * @property {boolean} isAuthenticated
 * @property {boolean} isLoading
 * @property {string|null} error
 * @property {Company|null} selectedCompany
 * @property {Site|null} selectedSite
 * @property {number|null} companyCount - how many firms the user belongs to; null until `/api/firm` loads
 * @property {(user: User, token: string) => void} setAuth
 * @property {() => void} logout
 * @property {(user: User) => void} setUser
 * @property {(token: string) => void} setToken
 * @property {(loading: boolean) => void} setLoading
 * @property {(error: string|null) => void} setError
 * @property {(company: Company) => void} setSelectedCompany
 * @property {(site: Site|null) => void} setSelectedSite
 * @property {(count: number|null) => void} setCompanyCount
 * @property {() => void} initialize
 */

export const useAuthStore = create((set) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  selectedCompany: null,
  selectedSite: null,
  // In-memory only: refetched with the firm list on every page load, so it never goes stale across
  // a company switch (which always does a full navigation).
  companyCount: null,

  setAuth: (user, token) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    set({ user, token, isAuthenticated: true, error: null });
  },

  logout: () => {
    clearAuthStorage();
    useSidebarVisibilityStore.getState().clear();
    useBulkImportJobStore.getState().clear();
    set({ user: null, token: null, isAuthenticated: false, error: null, selectedCompany: null, selectedSite: null, companyCount: null });
  },

  setUser: (user) => set({ user }),

  setToken: (token) => {
    localStorage.setItem(TOKEN_KEY, token);
    set({ token });
  },

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  setSelectedCompany: (company) => {
    localStorage.setItem(SELECTED_COMPANY_KEY, JSON.stringify(company));
    set({ selectedCompany: company });
  },

  // Only the id and name are kept — enough for the stores' consumers and for display before a reload.
  // `null` (a customer with no sites, or a pick still pending) removes the persisted copy.
  setSelectedSite: (site) => {
    if (site) {
      const stored = { haloPsaSiteId: site.haloPsaSiteId, siteName: site.siteName };
      localStorage.setItem(SELECTED_SITE_KEY, JSON.stringify(stored));
      set({ selectedSite: stored });
    } else {
      localStorage.removeItem(SELECTED_SITE_KEY);
      set({ selectedSite: null });
    }
  },

  setCompanyCount: (count) => set({ companyCount: count }),

  initialize: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);
    const storedCompany = localStorage.getItem(SELECTED_COMPANY_KEY);
    const storedSite = localStorage.getItem(SELECTED_SITE_KEY);
    if (token) {
      let user = null;
      try {
        user = storedUser ? JSON.parse(storedUser) : null;
      } catch {
        user = null;
      }

      let selectedCompany = null;
      try {
        selectedCompany = storedCompany ? JSON.parse(storedCompany) : null;
      } catch {
        selectedCompany = null;
      }

      let selectedSite = null;
      try {
        selectedSite = storedSite ? JSON.parse(storedSite) : null;
      } catch {
        selectedSite = null;
      }

      set({ token, user, isAuthenticated: true, selectedCompany, selectedSite });
    }
  },
}));
